import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, SegmentedControl, BRAND } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

type Item = { id: string; name: string; qr_token: string; sub: string | null };

const COLS = [2, 3, 4];
const A4 = { w: 595.28, h: 841.89 };
const MARGIN = 28;

// pdf-lib's standard fonts are WinAnsi-only; a name with a character outside that
// range (accented, Indonesian, emoji) makes drawText throw and the whole PDF fail
// silently — the "QR generator is broken" bug. Downgrade any unencodable glyph to
// its closest ASCII (or '?') before drawing so every label renders.
function winAnsi(s: string): string {
  return (s ?? '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '') // strip combining accents
    .replace(/[^\x20-\xFF]/g, '?'); // anything still outside Latin-1 → '?'
}

export function QrLabels() {
  const { t } = useTranslation();
  const [kind, setKind] = useState(0); // 0 = assets, 1 = tools
  const [items, setItems] = useState<Item[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [colIdx, setColIdx] = useState(1); // 3 columns
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      const data: Item[] = kind === 0
        ? ((await supabase.from('assets').select('id,name,qr_token,properties(name)').is('deleted_at', null).order('name')).data as any[] ?? [])
            .map((a) => ({ id: a.id, name: a.name, qr_token: a.qr_token, sub: a.properties?.name ?? null }))
        : ((await supabase.from('tools').select('id,name,qr_token,serial_number,inventory_categories(name)').is('deleted_at', null).order('name')).data as any[] ?? [])
            .map((tl) => ({ id: tl.id, name: tl.name, qr_token: tl.qr_token, sub: tl.inventory_categories?.name ?? tl.serial_number ?? null }));
      setItems(data);
      setSelected(new Set(data.map((a) => a.id)));
    })();
  }, [kind]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function generate() {
    setBusy(true); setErr('');
    try {
      const [{ PDFDocument, StandardFonts, rgb }, QR] = await Promise.all([import('pdf-lib'), import('qrcode')]); // lazy
      const list = items.filter((a) => selected.has(a.id));
      const cols = COLS[colIdx]!;
      const cellW = (A4.w - 2 * MARGIN) / cols;
      const cellH = 120;
      const rows = Math.floor((A4.h - 2 * MARGIN) / cellH);
      const perPage = cols * rows;

      const pdf = await PDFDocument.create();
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);
      const mono = await pdf.embedFont(StandardFonts.Courier);

      for (let i = 0; i < list.length; i++) {
        if (i % perPage === 0) pdf.addPage([A4.w, A4.h]);
        const page = pdf.getPage(pdf.getPageCount() - 1);
        const idx = i % perPage;
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const x = MARGIN + col * cellW;
        const yTop = A4.h - MARGIN - row * cellH;
        const a = list[i]!;

        const dataUrl = await QR.toDataURL(a.qr_token, { margin: 0, width: 300 });
        const png = await pdf.embedPng(await (await fetch(dataUrl)).arrayBuffer());
        const qrSize = 72;
        page.drawImage(png, { x: x + 8, y: yTop - qrSize - 10, width: qrSize, height: qrSize });
        page.drawText(winAnsi(a.name).slice(0, 24), { x: x + qrSize + 16, y: yTop - 28, size: 10, font, color: rgb(0.04, 0.06, 0.09) });
        page.drawText(winAnsi(a.sub ?? '').slice(0, 26), { x: x + qrSize + 16, y: yTop - 44, size: 8, font, color: rgb(0.4, 0.44, 0.52) });
        page.drawText(a.qr_token, { x: x + qrSize + 16, y: yTop - 62, size: 8, font: mono, color: rgb(0.23, 0.36, 0.99) });
        // brand mark — black & white version of the logo, bottom-left of the label
        const bx = x + 8;
        const by = yTop - cellH + 14;
        page.drawRectangle({ x: bx, y: by, width: 12, height: 12, color: rgb(0, 0, 0) });
        page.drawText(BRAND.symbolLetter, { x: bx + 2.8, y: by + 2.6, size: 9, font: fontBold, color: rgb(1, 1, 1) });
        page.drawText(winAnsi(BRAND.name), { x: bx + 17, y: by + 3, size: 7, font: fontBold, color: rgb(0, 0, 0) });
        page.drawRectangle({ x, y: yTop - cellH + 8, width: cellW - 8, height: cellH - 16, borderColor: rgb(0.9, 0.91, 0.93), borderWidth: 0.5 });
      }

      const bytes = await pdf.save();
      const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `qr-labels-${kind === 0 ? 'assets' : 'tools'}-a4.pdf`;
      link.click();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layout header={<Topbar />}>
      <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4 px-7 py-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('qr.labels')}</h1>
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <Card className="flex flex-wrap items-center gap-4 p-5">
          <SegmentedControl items={[t('nav.assets'), t('nav.tools')]} active={kind} onChange={setKind} />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">{t('qr.columns')}</span>
            <SegmentedControl items={COLS.map(String)} active={colIdx} onChange={setColIdx} />
          </div>
          <span className="text-sm text-muted">{selected.size} / {items.length}</span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" onClick={() => setSelected(new Set(items.map((a) => a.id)))}>{t('qr.selectAll')}</Button>
            <Button variant="accent" disabled={busy || selected.size === 0} onClick={generate}>{busy ? t('common.saving') : t('qr.generate')}</Button>
          </div>
        </Card>
        <Card className="overflow-hidden p-0">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-divider first:border-0">
                  <td className="w-10 px-5 py-2.5"><input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} /></td>
                  <td className="px-2 py-2.5 font-medium">{a.name}</td>
                  <td className="px-2 py-2.5 text-muted">{a.sub}</td>
                  <td className="px-5 py-2.5 font-mono text-xs text-muted">{a.qr_token}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </Layout>
  );
}
